<template>
  <div class="relative">
    <label class="block text-sm font-medium text-gray-700 mb-2">User Filter</label>
    <div class="relative">
      <input
        v-model="searchQuery"
        @input="onSearchInput"
        @focus="showDropdown = true"
        @blur="hideDropdown"
        type="text"
        placeholder="Search by name or ID..."
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-mocha-mousse-500 focus:border-mocha-mousse-500"
      />
      <div
        v-if="selectedUser"
        @click="clearSelection"
        class="absolute right-2 top-2.5 cursor-pointer text-gray-400 hover:text-gray-600"
      >
        <Icon icon="mdi:close" class="h-5 w-5" />
      </div>
    </div>

    <!-- Dropdown with search results -->
    <div
      v-if="showDropdown && (searchResults.length > 0 || isLoading)"
      class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
    >
      <div v-if="isLoading" class="px-3 py-2 text-sm text-gray-500">
        Searching...
      </div>
      <div v-else>
        <div
          v-for="user in searchResults"
          :key="user.vuo_id"
          @mousedown="selectUser(user)"
          class="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="font-medium text-gray-900">{{ user.username }}</div>
              <div class="text-xs text-gray-500">ID: {{ user.vuo_id }}</div>
            </div>
            <div class="flex items-center gap-2">
              <span
                v-if="user.match_type === 'exact'"
                class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800"
              >
                Exact
              </span>
              <span
                v-else
                class="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800"
              >
                Distance: {{ user.distance }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No results message -->
    <div
      v-if="showDropdown && !isLoading && searchQuery && searchResults.length === 0"
      class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
    >
      <div class="px-3 py-2 text-sm text-gray-500">
        No users found
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Icon } from '@iconify/vue'
import api from '@/services/api'

const emit = defineEmits(['user-selected'])

const searchQuery = ref('')
const searchResults = ref([])
const selectedUser = ref(null)
const showDropdown = ref(false)
const isLoading = ref(false)

let searchTimeout = null

const searchUsers = async (query) => {
  if (!query || query.length < 2) {
    searchResults.value = []
    return
  }

  try {
    isLoading.value = true
    const response = await api.searchUsers(query)
    searchResults.value = response.data.message.users || []
  } catch (error) {
    console.error('Failed to search users:', error)
    searchResults.value = []
  } finally {
    isLoading.value = false
  }
}

const onSearchInput = () => {
  // Clear previous selection when typing
  if (selectedUser.value && searchQuery.value !== selectedUser.value.username) {
    clearSelection()
  }

  // Clear existing timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  // Debounce search
  searchTimeout = setTimeout(() => {
    searchUsers(searchQuery.value)
  }, 300)
}

const selectUser = (user) => {
  selectedUser.value = user
  searchQuery.value = user.username
  showDropdown.value = false
  emit('user-selected', user.vuo_id)
}

const clearSelection = () => {
  selectedUser.value = null
  searchResults.value = []
  emit('user-selected', null)
}

const hideDropdown = () => {
  // Small delay to allow click events to register
  setTimeout(() => {
    showDropdown.value = false
  }, 200)
}

// Watch for external changes to the search query
watch(() => searchQuery.value, (newVal) => {
  if (!newVal) {
    clearSelection()
  }
})
</script>